import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle2, XCircle, Scale, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { reasonText } from "@/lib/moderationTexts";
import { toast } from "sonner";

// "Content under review" modal — opened when the author taps a moderation
// notification. Shows the LIVE status, a plain-language reason and the appeal button.
export const ModerationStatusModal = ({ open, onClose, meta }) => {
  const { t } = useApp();
  const m = meta || {};
  const [state, setState] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !m.target_type || !m.target_id) return;
    setState(null);
    api.get("/moderation/my-status", { params: { target_type: m.target_type, target_id: m.target_id } })
      .then(({ data }) => setState(data))
      .catch(() => setState({ status: m.status === "blocked" ? "queued" : (m.status || "queued"), categories: m.categories || [], appealed: false }));
  }, [open, m.target_type, m.target_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = state ? (state.status === "blocked" ? "queued" : state.status) : null;
  const cats = state?.categories?.length ? state.categories : (m.categories || []);

  const STATUS_UI = {
    queued: { Icon: Clock, color: "#B45309", bg: "#B453091a", label: t("modstatus.queued", "En cours de vérification par un modérateur") },
    removed: { Icon: XCircle, color: "#DC2626", bg: "#DC26261a", label: t("modstatus.removed", "Retiré — non conforme aux règles de la communauté") },
    restored: { Icon: CheckCircle2, color: "#16A34A", bg: "#16A34A1a", label: t("modstatus.restored", "Rétabli — de nouveau visible par tous") },
    deleted: { Icon: XCircle, color: "#6B7280", bg: "#6B72801a", label: t("modstatus.deleted", "Ce contenu n'existe plus") },
  };
  const ui = STATUS_UI[status] || STATUS_UI.queued;

  const sendAppeal = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/moderation/appeals", { target_type: m.target_type, target_id: m.target_id });
      setState((s) => ({ ...(s || {}), appealed: true }));
      toast.success(data.already
        ? t("appeal.already", "Un appel est déjà en cours pour ce contenu.")
        : t("appeal.sent", "Appel envoyé — un modérateur va réexaminer votre contenu."));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error", "Erreur"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="max-w-sm" data-testid="moderation-status-modal">
        <DialogHeader>
          <DialogTitle className="font-display">{t("modstatus.title", "Contenu en vérification")}</DialogTitle>
        </DialogHeader>
        {!state ? (
          <div className="py-8 grid place-items-center" data-testid="modstatus-loading">
            <Loader2 className="animate-spin" size={22} style={{ color: "var(--yalola-text-muted)" }} />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-xl p-3" style={{ background: ui.bg }} data-testid={`modstatus-${status}`}>
              <ui.Icon size={20} className="shrink-0 mt-0.5" style={{ color: ui.color }} />
              <div className="text-sm font-medium" style={{ color: ui.color }}>{ui.label}</div>
            </div>
            {status !== "deleted" && (
              <div className="text-sm" data-testid="modstatus-reason">
                <span className="font-semibold">{t("modstatus.reason", "Raison")} : </span>
                <span style={{ color: "var(--yalola-text-muted)" }}>{reasonText(cats, t)}</span>
              </div>
            )}
            {status === "queued" && (
              <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                {t("modstatus.process", "Notre équipe de modération vérifie ce contenu. Vous serez notifié du résultat. Si vous pensez qu'il respecte nos règles, vous pouvez faire appel.")}
              </p>
            )}
            {(status === "queued" || status === "removed") && (
              state.appealed ? (
                <div className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid="modstatus-appealed">
                  <Check size={13} /> {t("appeal.sent_short", "Appel envoyé")}
                </div>
              ) : (
                <Button className="pill-primary rounded-full w-full" onClick={sendAppeal} disabled={sending} data-testid="modstatus-appeal-btn">
                  {sending ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Scale size={15} className="mr-1.5" />}
                  {t("appeal.button", "Faire appel")}
                </Button>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
